import { useCallback, useEffect } from 'react'

import { CURRENT_ENVIRONMENT } from '~/shared/constants'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import { useCookieConsentAnswer } from '~/shared/hooks/useCookieConsentAnswer'
import {
  selectCookieConsentCountries,
  selectGoogleAnalyticsId,
} from '~/shared/store/common'
import { selectUserCountryCode } from '~/shared/store/user'
import { getCurrentEnvironment } from '~/shared/utils'

const GA_CONSENT_VALUE = {
  GRANTED: 'granted',
  DENIED: 'denied',
} as const

const GTM_ID = {
  [CURRENT_ENVIRONMENT.STAGE]: 'GTM-NJ225TH7',
  [CURRENT_ENVIRONMENT.PROD]: 'GTM-MLVBD5Z3',
}

const useSelectors = () =>
  useBulkSelector({
    googleAnalyticsId: selectGoogleAnalyticsId,
    userCountryCode: selectUserCountryCode,
    cookieConsentCountries: selectCookieConsentCountries,
  })

export const useGoogleAnalytics = (): void => {
  const { googleAnalyticsId, userCountryCode, cookieConsentCountries } =
    useSelectors()
  const { currentEnv } = getCurrentEnvironment()
  const gtmID = GTM_ID[currentEnv]

  const {
    isEUUser,
    cookieConsent,
    hasAdvertisingAndTargetingCookie,
    hasFunctionalCookie,
    hasAnalyticsAndPerformanceCookie,
  } = useCookieConsentAnswer()

  const setDefaultConsentOptions = useCallback(() => {
    if (!isEUUser) return

    const script = document.createElement('script')
    script.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
    
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    `
    document.body.appendChild(script)
  }, [isEUUser])

  const initGtag = useCallback(() => {
    const script = document.createElement('script')
    script.text = `window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('set', 'url_passthrough', true);
        `
    script.async = true
    document.body.appendChild(script)
  }, [])

  const setUpGTMForAdWords = useCallback(() => {
    const script = document.createElement('script')
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`
    script.async = true
    document.body.appendChild(script)
  }, [googleAnalyticsId])

  const setUpGTM = useCallback(() => {
    if (!gtmID) return

    const script = document.createElement('script')
    script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer', '${gtmID}');
      console.log('yep')
        `

    script.async = true
    document.body.appendChild(script)
  }, [gtmID])

  useEffect(() => {
    if (!googleAnalyticsId || !userCountryCode) return

    setDefaultConsentOptions()
    initGtag()
    setUpGTM()
    setUpGTMForAdWords()

    window.gtag('config', googleAnalyticsId, {
      send_page_view: false,
    })
  }, [
    googleAnalyticsId,
    initGtag,
    setDefaultConsentOptions,
    setUpGTMForAdWords,
    setUpGTM,
    userCountryCode,
  ])

  useEffect(() => {
    if (!isEUUser || !cookieConsent?.length || !window.gtag) return

    window.gtag('consent', 'update', {
      ad_user_data: hasAdvertisingAndTargetingCookie
        ? GA_CONSENT_VALUE.GRANTED
        : GA_CONSENT_VALUE.DENIED,
      ad_personalization:
        hasAdvertisingAndTargetingCookie && hasFunctionalCookie
          ? GA_CONSENT_VALUE.GRANTED
          : GA_CONSENT_VALUE.DENIED,
      ad_storage:
        hasAdvertisingAndTargetingCookie && hasFunctionalCookie
          ? GA_CONSENT_VALUE.GRANTED
          : GA_CONSENT_VALUE.DENIED,
      analytics_storage: hasAnalyticsAndPerformanceCookie
        ? GA_CONSENT_VALUE.GRANTED
        : GA_CONSENT_VALUE.DENIED,
      region: cookieConsentCountries,
    })
  }, [
    cookieConsent?.length,
    cookieConsentCountries,
    hasAdvertisingAndTargetingCookie,
    hasAnalyticsAndPerformanceCookie,
    hasFunctionalCookie,
    isEUUser,
  ])
}
